'use client';

import * as React from 'react';
import { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Pencil, Building2, MessageSquare } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useMediaQuery } from '@/doi-tac-module/hooks/use-media-query';
import { cn } from '@/doi-tac-module/lib/utils';

interface OwnerProfileHoverCardProps {
    user: any;
    language: string;
    trigger: React.ReactNode;
    job?: any;
}

function HoverCardInner({ user, language, job }: { user: any; language: string; job?: any }) {
    const [isClient, setIsClient] = useState(false);

    useEffect(() => { setIsClient(true); }, []);

    const agency = user?.agencyProfile;

    // --- Giải quyết Avatar ---
    let avatarSrc = user?.urlAvatar || '';
    if (agency?.representativePhoto) {
        avatarSrc = typeof agency.representativePhoto === 'string'
            ? agency.representativePhoto
            : (agency.representativePhoto as any)?.url || avatarSrc;
    } else if (agency?.companyLogo) {
        avatarSrc = typeof agency.companyLogo === 'string'
            ? agency.companyLogo
            : (agency.companyLogo as any)?.url || avatarSrc;
    }

    // --- Giải quyết Logo công ty ---
    let logoSrc = '';
    if (agency?.companyLogo) {
        logoSrc = typeof agency.companyLogo === 'string'
            ? agency.companyLogo
            : (agency.companyLogo as any)?.url || '';
    }

    const name = agency?.managingDirector || user?.fullName || 'Người dùng';
    const companyName = agency?.companyName || '';
    const address = agency?.officeAddress || '';
    const phone = agency?.mdPhone || agency?.officialPhone || '';
    const email = agency?.officialEmail || '';
    const zalo = agency?.zalo || phone;
    const facebook = agency?.facebookUrl || '';

    const formatZaloLink = (z: string) => {
        const number = z.replace(/\D/g, '');
        return number ? `https://zalo.me/${number}` : '#';
    };

    const formatFbLink = (fb: string) => {
        if (!fb) return '#';
        if (fb.startsWith('http')) return fb;
        return `https://facebook.com/${fb}`;
    };

    const zaloLink = formatZaloLink(zalo);
    const fbLink = formatFbLink(facebook);
    const phoneLink = phone ? `tel:${phone.replace(/\s/g, '')}` : '#';


    const labels = {
        address: { vi: 'Địa chỉ', bn: 'ঠিকানা' },
        phone: { vi: 'Điện thoại', bn: 'ফোন' },
        email: { vi: 'Email', bn: 'ইমেইল' },
        update: { vi: 'Cập nhật hồ sơ', bn: 'প্রোফাইল আপডেট করুন' },
        myContact: { vi: 'Liên hệ tôi', bn: 'যোগাযোগ করুন' }
    };
    const l = (key: keyof typeof labels) => (labels[key] as any)[language] || (labels[key] as any).vi;

    return (
        <div className="flex flex-col bg-white overflow-hidden rounded-xl">
            {/* Header - Avatar + Tên */}
            <div className="p-4 bg-gradient-to-br from-slate-50 to-white border-b border-slate-100 flex items-center gap-3">
                <Avatar className="h-12 w-12 border-2 border-white shadow-md shrink-0">
                    <AvatarImage src={avatarSrc} alt={name} className="object-cover" />
                    <AvatarFallback className="bg-slate-100 text-slate-500 text-sm font-bold">
                        {name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                </Avatar>
                <div className="flex flex-col min-w-0">
                    <span className="text-sm font-bold text-slate-900 leading-tight truncate">{name}</span>
                    {companyName ? (
                        <div className="flex items-center gap-1.5 mt-1">
                            {logoSrc ? (
                                <img src={logoSrc} alt="Logo" className="h-4 w-4 object-contain rounded" />
                            ) : (
                                <Building2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                            )}
                            <span className="text-[11px] text-slate-500 font-medium truncate">{companyName}</span>
                        </div>
                    ) : (
                        <span className="text-[11px] text-slate-400 mt-0.5">
                            {language === 'vi' ? 'Nhà tuyển dụng' : 'Employer'}
                        </span>
                    )}
                </div>
            </div>

            {/* Thông tin liên hệ */}
            <div className="p-3 space-y-2">
                {address && (
                    <div className="flex items-start gap-2.5">
                        <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                        <div className="flex flex-col min-w-0">
                            <span className="text-[9px] text-slate-400 font-medium uppercase tracking-wider">{l('address')}</span>
                            <span className="text-[11px] text-slate-600 leading-snug">{address}</span>
                        </div>
                    </div>
                )}
                {phone && (
                    <div className="flex items-center gap-2.5">
                        <Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <div className="flex flex-col">
                            <span className="text-[9px] text-slate-400 font-medium uppercase tracking-wider">{l('phone')}</span>
                            <span className="text-[11px] text-slate-600">{phone}</span>
                        </div>
                    </div>
                )}
                {email && (
                    <div className="flex items-center gap-2.5">
                        <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <div className="flex flex-col min-w-0">
                            <span className="text-[9px] text-slate-400 font-medium uppercase tracking-wider">{l('email')}</span>
                            <span className="text-[11px] text-slate-600 truncate">{email}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer - Nút liên hệ + Link cập nhật */}
            {isClient && (
                <div className="px-3 pb-3 pt-1 border-t border-slate-50 space-y-2">
                    <div className="flex items-center gap-1.5">
                        {fbLink !== '#' && (
                            <Button asChild variant="outline" size="icon" className="h-8 w-8 border-blue-500 text-blue-500 hover:bg-blue-50">
                                <Link href={fbLink} target="_blank" onClick={(e) => e.stopPropagation()}>
                                    <Image src="/img/BANGLADESH/TRANGDOITAC/Facebook.svg" alt="Facebook" width={18} height={18} />
                                </Link>
                            </Button>
                        )}
                        {zaloLink !== '#' && (
                            <Button asChild variant="outline" size="icon" className="h-8 w-8 border-[#0068FF] text-[#0068FF] hover:bg-blue-50">
                                <Link href={zaloLink} target="_blank" onClick={(e) => e.stopPropagation()}>
                                    <Image src="/img/Zalo.svg" alt="Zalo" width={18} height={18} />
                                </Link>
                            </Button>
                        )}
                        {phoneLink !== '#' && (
                            <Button asChild variant="outline" size="icon" className="h-8 w-8 border-green-500 text-green-500 hover:bg-green-50">
                                <Link href={phoneLink} onClick={(e) => e.stopPropagation()}>
                                    <Image src="/img/phone.svg" alt="Phone" width={18} height={18} />
                                </Link>
                            </Button>
                        )}
                        <Button variant="outline" size="icon" className="h-8 w-8 border-primary text-primary hover:bg-primary/5 cursor-not-allowed opacity-70">
                            <MessageSquare className="h-4 w-4" />
                        </Button>
                    </div>
                    <Link
                        href="/cap-nhat-ho-so"
                        className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-primary transition-colors"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Pencil className="h-3 w-3" />
                        {l('update')}
                    </Link>
                </div>
            )}
        </div>
    );
}

export function OwnerProfileHoverCard({ user, language, trigger, job }: OwnerProfileHoverCardProps) {
    const isMobile = useMediaQuery('(max-width: 768px)');

    if (isMobile) {
        return (
            <Popover>
                <PopoverTrigger asChild>
                    <div onClick={(e) => e.stopPropagation()}>{trigger}</div>
                </PopoverTrigger>
                <PopoverContent
                    className="w-72 p-0 overflow-hidden border-slate-200 shadow-2xl bg-white rounded-xl z-50"
                    side="top"
                    align="start"
                >
                    <HoverCardInner user={user} language={language} job={job} />
                </PopoverContent>
            </Popover>
        );
    }

    return (
        <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
                {trigger}
            </HoverCardTrigger>
            <HoverCardContent
                className="w-72 p-0 overflow-hidden border-slate-200 shadow-2xl bg-white rounded-xl z-50"
                side="top"
                align="start"
            >
                <HoverCardInner user={user} language={language} job={job} />
            </HoverCardContent>
        </HoverCard>
    );
}
